import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const FIELDS = [
  ["quotation_number", "Quotation number *", "e.g. BRL/QT/2026/014"],
  ["client_name", "Client / customer name *", "Client name"],
  ["project_name", "Project name *", "Project name"],
  ["location", "Project location / address", "City, address"],
  ["prepared_by", "Proposal prepared by", "Your name"],
];

export function CreateQuotationDialog({ open, onOpenChange, onCreate, defaultPreparedBy }) {
  const [form, setForm] = useState({ prepared_by: defaultPreparedBy || "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const ready = ["quotation_number", "client_name", "project_name"].every((f) => (form[f] || "").trim());

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onCreate({ ...form, quotation_date: new Date().toISOString().slice(0, 10) });
      setForm({ prepared_by: defaultPreparedBy || "" });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="qg-create-dialog">
        <DialogHeader><DialogTitle>New quotation</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {FIELDS.map(([f, label, ph]) => (
            <div key={f} className="space-y-1.5">
              <Label htmlFor={`qg-create-${f}`}>{label}</Label>
              <Input id={`qg-create-${f}`} value={form[f] || ""} placeholder={ph} data-testid={`qg-create-${f}-input`}
                onChange={(e) => setForm((p) => ({ ...p, [f]: e.target.value }))} className="rounded-lg" />
            </div>
          ))}
          <p className="text-xs text-slate-500">All 12 BOQ sections load from the Brillen template automatically.</p>
          {error && <p className="text-sm text-red-600" data-testid="qg-create-error">{error}</p>}
          <DialogFooter>
            <Button type="submit" disabled={!ready || busy} data-testid="qg-create-submit-btn" className="bg-brillien-600 hover:bg-brillien-700 text-white rounded-lg">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create quotation
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function NumberPromptDialog({ open, onOpenChange, title, onSubmit }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try { await onSubmit(value.trim()); setValue(""); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="qg-number-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Label htmlFor="qg-new-number">New quotation number *</Label>
          <Input id="qg-new-number" value={value} onChange={(e) => setValue(e.target.value)} data-testid="qg-number-input" className="rounded-lg" />
          {error && <p className="text-sm text-red-600" data-testid="qg-number-error">{error}</p>}
          <DialogFooter>
            <Button type="submit" disabled={!value.trim() || busy} data-testid="qg-number-submit-btn" className="bg-brillien-600 hover:bg-brillien-700 text-white rounded-lg">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Duplicate
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
